import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { ActionButton, HasAccessTo, InputFieldFactory, SearchInput } from 'app/components';
import { CONNECTION } from 'app/common/Authorization/entities';
import { RUN } from 'app/common/Authorization/permissions';
import { TranslationKeys } from 'app/translations';
import { CAN_COMPARE, CAN_COMPARE_COMPLETED } from '../../../../../../../ConnectionDetailsPage/BatchesTab/constants';
import { DropdownStyled } from '../../../../../../../../Components/EventsToolbar/EventsToolbar.styles';
import { useIntl } from 'react-intl';

type CompareBatchListHeaderProps = {
  isLoading?: boolean;
  onSearch: (inputValue: string) => void;
  searchQuery?: string;
  onFilterMutationType: (filter: string) => void;
  approveBatch: () => void;
  archiveBatch: () => void;
  markAsComplete: () => void;
  resendMutations: () => void;
  rejectMutations: () => void;
  declineBatch: () => void;
  batchStatus: 'draft' | 'pending-insurer-data' | 'compared' | 'to-be-checked';
  selectedCount?: number;
  batchSize: number;
};

export const CompareBatchListHeader = ({
  onSearch,
  onFilterMutationType,
  searchQuery,
  approveBatch,
  archiveBatch,
  markAsComplete,
  resendMutations,
  rejectMutations,
  declineBatch,
  batchStatus,
  selectedCount = 0,
  batchSize,
  isLoading = false,
}: CompareBatchListHeaderProps) => {
  const intl = useIntl();
  const canRunConnections = HasAccessTo(CONNECTION, RUN);
  const disabledApproveButton = isLoading || selectedCount === 0 || !canRunConnections;
  const [position, setPosition] = useState({
    position: 'unset',
    backgroundColor: 'none',
    top: '0%',
    margin: '0 auto',
    padding: 'unset',
    width: 'unset',
    marginLeft: 'unset',
  });

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 340) {
        setPosition({
          margin: '0 auto',
          position: 'fixed',
          top: '64px',
          backgroundColor: '#E4F4F9',
          width: '-webkit-fill-available',
          padding: '10px 24px 10px 24px',
          marginLeft: '-24px !important',
        });
      } else {
        setPosition({
          margin: '0 auto',
          position: 'unset',
          top: 'unset',
          backgroundColor: 'unset',
          width: 'unset',
          padding: 'unset',
          marginLeft: 'unset',
        });
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const onChangeSearchInput = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSearch(event.target.value);
    },
    [onSearch],
  );

  const onChangeMutationTypeFilter = React.useCallback(
    ({ value }) => {
      onFilterMutationType(value);
    },
    [onFilterMutationType],
  );

  const dropdownOptions = {
    reject: [
      {
        title: useIntl().formatMessage({ id: 'connection.processes.compareBatch.rejectMutationsButton' }),
        tooltip: useIntl().formatMessage({ id: 'tooltip_events_nothing_selected' }),
        confirmDialog: {
          title: useIntl().formatMessage({ id: 'connection.processes.compareBatch.rejectConfirmTitle' }),
          message: useIntl().formatMessage({ id: 'connection.processes.compareBatch.rejectConfirmMessage' }),
        },
        action: rejectMutations,
        disabled: disabledApproveButton,
      },
      {
        title: useIntl().formatMessage({ id: 'connection.processes.compareBatch.rejectAllMutationsButton' }),
        tooltip: useIntl().formatMessage({ id: 'tooltip_events_nothing_selected' }),
        confirmDialog: {
          title: useIntl().formatMessage({ id: 'connection.processes.compareBatch.rejectConfirmTitle' }),
          message: useIntl().formatMessage({ id: 'connection.processes.compareBatch.rejectAllConfirmMessage' }),
        },
        action: declineBatch,
        disabled: batchSize === 0,
      },
    ],
  };

  const mutationsSelectItemsWithAllOption = React.useMemo(() => {
    return [
      {
        value: '',
        element: intl.formatMessage({ id: TranslationKeys.all }),
        default: true,
      },
      {
        value: 'UNKNOWN',
        element: intl.formatMessage({ id: TranslationKeys.global_selectUnknown }),
      },
    ];
  }, [intl]);

  return (
    <Box
      display={'flex'}
      justifyContent={'space-between'}
      alignItems={'center'}
      gap={2}
      sx={{
        position,
        zIndex: 1000,
      }}
    >
      <InputFieldFactory
        key={`mutation-scheme-select`}
        disabled={isLoading}
        onChange={onChangeMutationTypeFilter}
        field={{
          type: 'select',
          name: 'MutationTypeFilter',
          label: intl.formatMessage({ id: TranslationKeys.mutation_type }),
          fullWidth: false,
          value: null,
          items: mutationsSelectItemsWithAllOption,
        }}
      />

      <SearchInput
        disabled={isLoading}
        onChange={onChangeSearchInput}
        initialSearchQuery={searchQuery}
        debounceTime={500}
      />

      {CAN_COMPARE.includes(batchStatus) && (
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
          <DropdownStyled options={dropdownOptions.reject} intl />
          <ActionButton
            messageId={TranslationKeys.connection_processes_compareBatch_archiveButton}
            disabled={disabledApproveButton}
            onClick={archiveBatch}
          />
          <ActionButton
            messageId={TranslationKeys.connection_processes_compareBatch_approveButton}
            disabled={disabledApproveButton}
            onClick={approveBatch}
          />
        </Box>
      )}

      {CAN_COMPARE_COMPLETED.includes(batchStatus) && (
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
          <DropdownStyled options={dropdownOptions.reject} intl />
          <ActionButton
            messageId={TranslationKeys.connection_processes_compareBatch_resendSelectedMutations}
            disabled={disabledApproveButton}
            onClick={resendMutations}
          />
          <ActionButton
            messageId={TranslationKeys.connection_processes_compareBatch_markAsComplete}
            disabled={false}
            onClick={markAsComplete}
          />
        </Box>
      )}
    </Box>
  );
};
